/**
 * Robust JSON extractor for LLM responses.
 * Chain: direct parse → strip fences → extract first {...} block → partial extraction
 * Never throws — returns null if all strategies fail.
 */

import type { AnalysisResult, ImportResult, Verdict } from "./types";

type ParseTarget = AnalysisResult | ImportResult;

export function extractJSON<T extends ParseTarget>(raw: string): T | null {
  if (!raw) return null;

  // Normalize special characters that break JSON strings
  const normalized = raw
    .replace(/[\u2013\u2014]/g, "-")   // em-dash, en-dash
    .replace(/[\u201C\u201D]/g, '"')   // curly double quotes
    .replace(/[\u2018\u2019]/g, "'");  // curly single quotes

  // Strategy 1: direct parse after stripping markdown fences
  const stripped = normalized.replace(/```(?:json)?[\s\S]*?```/g, "").trim();
  try {
    return JSON.parse(stripped) as T;
  } catch {}

  // Strategy 2: extract first complete JSON object
  const match = stripped.match(/\{[\s\S]*\}/);
  if (match) {
    try {
      return JSON.parse(match[0]) as T;
    } catch {}
  }

  // Strategy 3: extract just the critical fields via regex for graceful degradation
  const verdict = normalized.match(/"verdict"\s*:\s*"([^"]+)"/)?.[1] as Verdict;
  const formal = normalized.match(/"formal_name"\s*:\s*"([^"]+)"/)?.[1];
  const note = normalized.match(/"verdict_note"\s*:\s*"([^"]+)"/)?.[1];

  if (verdict) {
    // Return a minimal valid AnalysisResult so the UI doesn't crash
    return {
      step1: {
        name: "Partial parse",
        content:
          "Full analysis could not be parsed. The model response was malformed. " +
          "Raw content preview: " +
          raw.slice(0, 300),
      },
      step2: { name: "Logical operators", content: "", skip: true },
      step3: { name: "Quantifiers", content: "", skip: true },
      step4: { name: "Modal operators", content: "", skip: true },
      step5: { name: "Inference structure", content: "", skip: true },
      step6: { name: "Validity", content: "", skip: true },
      step7: { name: "Failure modes", content: "", failures: [] },
      presuppositions: [],
      verdict,
      verdict_note: note ?? "Partial result — model response was malformed.",
      formal_name: formal ?? "—",
      new_terms: [],
    } as unknown as T;
  }

  return null;
}

/**
 * Detect whether a long input should be auto-routed to Import mode.
 */
export function shouldAutoImport(text: string): boolean {
  const clauses = text.split(/[.;]/).filter((s) => s.trim().length > 20);
  return clauses.length >= 3;
}
